import { useState } from 'react'
import { Card, Button, Space, Typography, Divider, Tag, Alert, Tabs } from 'antd'
import { useAuthStore } from '~/app/services/auth/auth.store'
import { useTenant } from '~/app/contexts'
import { Tenant } from '~/app/services/auth/auth.type'
import ColorShowcase from '~/app/components/ColorShowcase'

const { Title, Text, Paragraph } = Typography

export default function TestPage() {
  const [testResults, setTestResults] = useState<Record<string, boolean>>({})

  // Auth Store
  const {
    accessToken,
    refreshToken,
    expiry,
    tempToken,
    tenants,
    selectedTenant,
    setAuth,
    setTempAuth,
    setSelectedTenant,
    clearAuth,
  } = useAuthStore()

  // Tenant Context
  const { tenantConfig, isLoadingConfig } = useTenant()

  // Test 1: Auth Store Persistence
  const testAuthPersistence = () => {
    try {
      // Set auth data
      setAuth('test-access-token', 'test-refresh-token', '2025-12-31T23:59:59Z')

      // Check if it's set
      const state = useAuthStore.getState()
      const success =
        state.accessToken === 'test-access-token' &&
        state.refreshToken === 'test-refresh-token' &&
        state.expiry === '2025-12-31T23:59:59Z'

      setTestResults((prev) => ({ ...prev, authPersistence: success }))

      // Check localStorage
      const stored = localStorage.getItem('auth')
      console.log('Stored auth data:', stored)
    } catch (error) {
      console.error('Auth persistence test failed:', error)
      setTestResults((prev) => ({ ...prev, authPersistence: false }))
    }
  }

  // Test 2: Temp Auth & Tenants
  const testTempAuth = () => {
    try {
      const mockTenants: Tenant[] = [
        { id: '1', name: 'Test Tenant 1', slug: 'test-1' },
        { id: '2', name: 'Test Tenant 2', slug: 'test-2' },
      ]

      setTempAuth('temp-token-123', mockTenants)

      const state = useAuthStore.getState()
      const success =
        state.tempToken === 'temp-token-123' &&
        state.tenants.length === 2 &&
        state.tenants[0].name === 'Test Tenant 1'

      setTestResults((prev) => ({ ...prev, tempAuth: success }))
    } catch (error) {
      console.error('Temp auth test failed:', error)
      setTestResults((prev) => ({ ...prev, tempAuth: false }))
    }
  }

  // Test 3: Select Tenant
  const testSelectTenant = () => {
    try {
      const mockTenant: Tenant = { id: '1', name: 'Test Tenant 1', slug: 'test-1' }
      setSelectedTenant(mockTenant)

      const state = useAuthStore.getState()
      const success =
        state.selectedTenant?.id === '1' && state.selectedTenant?.name === 'Test Tenant 1'

      setTestResults((prev) => ({ ...prev, selectTenant: success }))
    } catch (error) {
      console.error('Select tenant test failed:', error)
      setTestResults((prev) => ({ ...prev, selectTenant: false }))
    }
  }

  // Test 4: Clear Auth
  const testClearAuth = () => {
    try {
      clearAuth()

      const state = useAuthStore.getState()
      const success =
        state.accessToken === null &&
        state.refreshToken === null &&
        state.tempToken === null &&
        state.tenants.length === 0 &&
        state.selectedTenant === null

      setTestResults((prev) => ({ ...prev, clearAuth: success }))
    } catch (error) {
      console.error('Clear auth test failed:', error)
      setTestResults((prev) => ({ ...prev, clearAuth: false }))
    }
  }

  // Run all tests
  const runAllTests = () => {
    testAuthPersistence()
    setTimeout(() => testTempAuth(), 100)
    setTimeout(() => testSelectTenant(), 200)
    setTimeout(() => testClearAuth(), 300)
  }

  const getResultTag = (result: boolean | undefined) => {
    if (result === undefined) return <Tag>Not Run</Tag>
    return result ? <Tag color="success">✓ Pass</Tag> : <Tag color="error">✗ Fail</Tag>
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <Title level={2}>🧪 Core Infrastructure Test Page</Title>
      <Paragraph>
        This page tests the migrated core infrastructure including auth store, tenant context, and
        API configuration.
      </Paragraph>

      <Divider />

      <Tabs
        defaultActiveKey="tests"
        items={[
          {
            key: 'tests',
            label: '🧪 Infrastructure Tests',
            children: (
              <div>

      {/* Test Controls */}
      <Card title="Test Controls" className="mb-4">
        <Space>
          <Button type="primary" onClick={runAllTests}>
            Run All Tests
          </Button>
          <Button onClick={() => setTestResults({})}>Clear Results</Button>
        </Space>
      </Card>

      {/* Test Results */}
      <Card title="Test Results" className="mb-4">
        <Space direction="vertical" className="w-full">
          <div className="flex justify-between items-center">
            <Text strong>1. Auth Store Persistence</Text>
            {getResultTag(testResults.authPersistence)}
          </div>
          <div className="flex justify-between items-center">
            <Text strong>2. Temp Auth & Tenants</Text>
            {getResultTag(testResults.tempAuth)}
          </div>
          <div className="flex justify-between items-center">
            <Text strong>3. Select Tenant</Text>
            {getResultTag(testResults.selectTenant)}
          </div>
          <div className="flex justify-between items-center">
            <Text strong>4. Clear Auth</Text>
            {getResultTag(testResults.clearAuth)}
          </div>
        </Space>
      </Card>

      {/* Current State */}
      <Card title="Current Auth State" className="mb-4">
        <Space direction="vertical" className="w-full">
          <div>
            <Text strong>Access Token: </Text>
            <Text code>{accessToken || 'null'}</Text>
          </div>
          <div>
            <Text strong>Refresh Token: </Text>
            <Text code>{refreshToken || 'null'}</Text>
          </div>
          <div>
            <Text strong>Expiry: </Text>
            <Text code>{expiry || 'null'}</Text>
          </div>
          <div>
            <Text strong>Temp Token: </Text>
            <Text code>{tempToken || 'null'}</Text>
          </div>
          <div>
            <Text strong>Tenants: </Text>
            <Text code>{JSON.stringify(tenants, null, 2)}</Text>
          </div>
          <div>
            <Text strong>Selected Tenant: </Text>
            <Text code>{JSON.stringify(selectedTenant, null, 2)}</Text>
          </div>
        </Space>
      </Card>

      {/* Tenant Context State */}
      <Card title="Tenant Context State" className="mb-4">
        <Space direction="vertical" className="w-full">
          <div>
            <Text strong>Loading Config: </Text>
            <Text code>{String(isLoadingConfig)}</Text>
          </div>
          <div>
            <Text strong>Tenant Config: </Text>
            <Text code>{JSON.stringify(tenantConfig, null, 2)}</Text>
          </div>
          {!selectedTenant && (
            <Alert
              message="No tenant selected"
              description="Tenant config will only load when a tenant is selected and auth token is present."
              type="info"
              showIcon
            />
          )}
        </Space>
      </Card>

      {/* Environment Variables */}
      <Card title="Environment Variables" className="mb-4">
        <Space direction="vertical" className="w-full">
          <div>
            <Text strong>VITE_API_URL: </Text>
            <Text code>{import.meta.env.VITE_API_URL || 'Not set'}</Text>
          </div>
          <div>
            <Text strong>VITE_SOCKET_URL: </Text>
            <Text code>{import.meta.env.VITE_SOCKET_URL || 'Not set'}</Text>
          </div>
          <div>
            <Text strong>VITE_ENV: </Text>
            <Text code>{import.meta.env.VITE_ENV || 'Not set'}</Text>
          </div>
        </Space>
      </Card>

      {/* Manual Test Actions */}
      <Card title="Manual Test Actions">
        <Space direction="vertical" className="w-full">
          <Space wrap>
            <Button onClick={testAuthPersistence}>Test Auth Persistence</Button>
            <Button onClick={testTempAuth}>Test Temp Auth</Button>
            <Button onClick={testSelectTenant}>Test Select Tenant</Button>
            <Button onClick={testClearAuth}>Test Clear Auth</Button>
          </Space>
          <Divider />
          <Space wrap>
            <Button
              onClick={() => {
                console.log('Current auth state:', useAuthStore.getState())
                console.log('LocalStorage auth:', localStorage.getItem('auth'))
              }}
            >
              Log State to Console
            </Button>
            <Button
              onClick={() => {
                localStorage.clear()
                window.location.reload()
              }}
              danger
            >
              Clear LocalStorage & Reload
            </Button>
          </Space>
        </Space>
      </Card>
              </div>
            ),
          },
          {
            key: 'colors',
            label: '🎨 Color System',
            children: <ColorShowcase />,
          },
        ]}
      />
    </div>
  )
}
